/**
 * iter251 — SLA chips for the support queue.
 *
 * `SlaChip` is the per-ticket countdown. `TriageBudgetMeter` is the header
 * readout of today's AI spend. Both read values the server computed — nothing
 * here re-derives SLA state, so the list and the digest email can never
 * disagree.
 */
import React from 'react';
import { Timer, AlertTriangle, CheckCircle2, Sparkles, PauseCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const fmtMinutes = (minutes) => {
  const n = Math.abs(Math.round(minutes ?? 0));
  if (n < 60) return `${n}m`;
  if (n < 1440) return `${Math.floor(n / 60)}h ${n % 60}m`;
  return `${Math.floor(n / 1440)}d ${Math.floor((n % 1440) / 60)}h`;
};

const STATE_META = {
  ok: { icon: Timer, chip: 'bg-emerald-950/30 border-emerald-900 text-emerald-300' },
  at_risk: { icon: AlertTriangle, chip: 'bg-amber-950/40 border-amber-700 text-amber-200' },
  breached: { icon: AlertTriangle, chip: 'bg-rose-950/50 border-rose-700 text-rose-200' },
  met: { icon: CheckCircle2, chip: 'bg-emerald-950/30 border-emerald-900 text-emerald-300' },
};

export const SlaChip = ({ sla, testId }) => {
  if (!sla || !STATE_META[sla.state]) return null;
  const meta = STATE_META[sla.state];
  const Icon = meta.icon;

  let label;
  if (sla.state === 'met') label = `Answered in ${fmtMinutes(sla.elapsed_minutes)}`;
  else if (sla.state === 'breached' && !sla.clock_running) label = `Late by ${fmtMinutes(sla.remaining_minutes)}`;
  else if (sla.state === 'breached') label = `Overdue ${fmtMinutes(sla.remaining_minutes)}`;
  else label = `${fmtMinutes(sla.remaining_minutes)} left`;

  return (
    <Badge
      className={`${meta.chip} border font-medium text-[10px] tabular-nums`}
      title={
        `Target ${fmtMinutes(sla.target_minutes)}`
        + (sla.provisional ? ' (untriaged — measured against High until the AI assigns a severity)' : '')
      }
      data-testid={testId}
    >
      <Icon className="h-3 w-3 mr-1" />
      {label}
      {sla.provisional && <span className="ml-1 opacity-60">?</span>}
    </Badge>
  );
};

export const TriageBudgetMeter = ({ budget, onClick }) => {
  if (!budget) return null;
  const pct = budget.day_cap > 0
    ? Math.min(100, (budget.day_used / budget.day_cap) * 100)
    : 0;
  const paused = !!budget.auto_replies_paused;
  const hot = pct >= 80;
  const barColor = paused ? 'bg-rose-500' : hot ? 'bg-amber-500' : 'bg-violet-500';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-md border px-3 py-2 transition-colors ${
        paused
          ? 'border-rose-800 bg-rose-950/30 hover:bg-rose-950/50'
          : 'border-[#1F2A4D] bg-[#0F1730] hover:bg-[#0A1024]'
      }`}
      data-testid="admin-support-budget-meter"
    >
      <div className="flex items-center gap-2 mb-1.5">
        {paused
          ? <PauseCircle className="h-3.5 w-3.5 text-rose-400" />
          : <Sparkles className="h-3.5 w-3.5 text-violet-400" />}
        <span className="text-[11px] font-medium text-slate-200">AI triage budget</span>
        <span
          className="text-[11px] text-slate-400 tabular-nums ml-auto pl-2"
          data-testid="admin-support-budget-usage"
        >
          {budget.day_used}/{budget.day_cap}
        </span>
      </div>
      <div className="h-1 w-40 rounded-full bg-[#0A1024] overflow-hidden">
        <div
          className={`h-full rounded-full ${barColor} transition-[width] duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p
        className={`text-[10px] mt-1 ${paused ? 'text-rose-300' : 'text-slate-500'}`}
        data-testid="admin-support-budget-status"
      >
        {paused
          ? 'Exhausted — auto-replies paused, tickets go to humans'
          : `${budget.hour_remaining} left this hour · tap to edit`}
      </p>
    </button>
  );
};

export default SlaChip;
